import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import AlarmRoundedIcon from "@mui/icons-material/AlarmRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const GradientCard = styled(Card)(({ theme, gradient }) => ({
  background: gradient,
  color: theme.palette.common.white,
  boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
  border: 0,
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: "0 18px 40px rgba(0,0,0,0.06)",
  border: `1px solid ${theme.palette.divider}`,
}));

const kpiCards = [
  {
    title: "Polizze Attive",
    value: "3",
    helper: "Coperture in corso",
    gradient: "linear-gradient(135deg, #1c75ff, #4b9dff)",
    icon: <ShieldRoundedIcon fontSize="large" />,
  },
  {
    title: "In Scadenza",
    value: "0",
    helper: "Prossimi 30 giorni",
    gradient: "linear-gradient(135deg, #3ac6a8, #7ae9b4)",
    icon: <CalendarMonthRoundedIcon fontSize="large" />,
  },
  {
    title: "Punti Loyalty",
    value: "1250",
    helper: "Livello Silver",
    gradient: "linear-gradient(135deg, #ffb26b, #ff9f43)",
    icon: <EmojiEventsRoundedIcon fontSize="large" />,
  },
  {
    title: "Crescita Anno",
    value: "+12%",
    helper: "Coperture totali",
    gradient: "linear-gradient(135deg, #14c26e, #10ac7d)",
    icon: <TrendingUpRoundedIcon fontSize="large" />,
  },
];

const notifications = [
  {
    title: "Polizza RCA in scadenza",
    body: "La tua polizza RCA BMW Serie 1 scade il 5 gennaio 2025. Rinnova ora per mantenere la copertura.",
    chip: "Urgente",
    tone: "warning",
    cta: "Rinnova Ora",
  },
  {
    title: "Punti Loyalty Disponibili",
    body: "Hai accumulato abbastanza punti per riscattare un premio! Dai un'occhiata al nostro catalogo.",
    chip: "Info",
    tone: "info",
    cta: "Vedi Premi",
  },
  {
    title: "Nuova Offerta Casa Disponibile",
    body: "Abbiamo una nuova offerta per polizze casa con sconti fino al 20%. Scopri i dettagli.",
    chip: "Novita",
    tone: "success",
    cta: "Scopri",
  },
];

const suggestions = [
  {
    title: "Polizza Casa Consigliata",
    badge: "Alta Priorita",
    why: "Hai solo copertura auto, ma nessuna protezione per la casa.",
    benefit: "Risparmio 15% + protezione completa",
    color: "#1c75ff",
    icon: <HomeRoundedIcon />,
  },
  {
    title: "Ottimizza la tua Polizza Vita",
    badge: "Consigliato",
    why: "Analisi del mercato mostra opzioni migliori disponibili.",
    benefit: "+30% copertura a parita di prezzo",
    color: "#13b86d",
    icon: <FavoriteRoundedIcon />,
  },
  {
    title: "Protezione Eventi Naturali",
    badge: "Consigliato",
    why: "Analisi rischi territoriali e climatici.",
    benefit: "Protezione completa da eventi estremi",
    color: "#ff7b22",
    icon: <AlarmRoundedIcon />,
  },
];

const redeemed = [
  {
    title: "Buono Sconto Carrefour 50 EUR",
    status: "consegnato",
    date: "13 set 2025",
  },
  {
    title: "Consulenza Legale Gratuita",
    status: "in_preparazione",
    date: "13 set 2025",
  },
];

function Personale() {
  const [casaDialogOpen, setCasaDialogOpen] = useState(false);
  const [vitaDialogOpen, setVitaDialogOpen] = useState(false);
  const [casaStep, setCasaStep] = useState(0);
  const [vitaStep, setVitaStep] = useState(0);
  const [errors, setErrors] = useState({
    nome: false,
    cognome: false,
    email: false,
    telefono: false,
    tipoAbitazione: false,
    metriQuadri: false,
    annoCostruzione: false,
    dataNascita: false,
    privacyAccettata: false,
    codiceFiscale: false,
  });
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    codiceFiscale: "",
    provincia: "",
    indirizzo: "",
    tipoAbitazione: "",
    metriQuadri: "",
    annoCostruzione: "",
    dataNascita: "",
    privacyAccettata: false,
    garanzie: {
      incendio: true,
      furto: true,
      danniAcqua: false,
      responsabilitaCivile: false,
    },
  });
  
  // Stato per garanzie vita
  const [vitaGaranzie, setVitaGaranzie] = useState({
    decesso: {
      enabled: true,
      durata: 10,
      capitale: 100000,
    },
    invalidita: {
      enabled: false,
      durata: 10,
      capitale: 100000,
    },
    malattiaGrave: {
      enabled: false,
      durata: 10,
      capitale: 5000,
    },
    inabilita: {
      enabled: false,
      durata: 10,
      capitale: 1000,
    },
  });

  const handleOpenCasaDialog = () => {
    setCasaDialogOpen(true);
    setCasaStep(0);
  };

  const handleCloseCasaDialog = () => {
    setCasaDialogOpen(false);
  };

  const handleOpenVitaDialog = () => {
    setVitaDialogOpen(true);
    setVitaStep(0);
  };

  const handleCloseVitaDialog = () => {
    setVitaDialogOpen(false);
    setVitaStep(0);
    
    // Reset form data
    setFormData({
      ...formData,
      dataNascita: "",
      privacyAccettata: false,
    });
    
    // Reset errors
    setErrors({
      ...errors,
      dataNascita: false,
      privacyAccettata: false,
    });
    
    // Reset garanzie vita
    setVitaGaranzie({
      decesso: {
        enabled: true,
        durata: 10,
        capitale: 100000,
      },
      invalidita: {
        enabled: false,
        durata: 10,
        capitale: 100000,
      },
      malattiaGrave: {
        enabled: false,
        durata: 10,
        capitale: 5000,
      },
      inabilita: {
        enabled: false,
        durata: 10,
        capitale: 1000,
      },
    });
  };

  const handleCheckboxPrivacyChange = (event) => {
    setFormData({ ...formData, privacyAccettata: event.target.checked });
  };

  const handleCalcolaPreventivo = () => {
    const newErrors = {
      ...errors,
      dataNascita: !formData.dataNascita,
      privacyAccettata: !formData.privacyAccettata,
    };

    setErrors(newErrors);

    if (newErrors.dataNascita || newErrors.privacyAccettata) {
      return;
    }

    // Passa allo step 2 (personalizzazione garanzie)
    setVitaStep(1);
  };
  
  const handleGaranziaToggle = (garanzia) => (event) => {
    setVitaGaranzie({
      ...vitaGaranzie,
      [garanzia]: {
        ...vitaGaranzie[garanzia],
        enabled: event.target.checked,
      },
    });
  };
  
  const handleSliderChange = (garanzia, field) => (event, newValue) => {
    setVitaGaranzie({
      ...vitaGaranzie,
      [garanzia]: {
        ...vitaGaranzie[garanzia],
        [field]: newValue,
      },
    });
  };
  
  const calcolaPremioTotale = () => {
    let totale = 0;
    if (vitaGaranzie.decesso.enabled) {
      totale += 180; // Base per decesso
    }
    if (vitaGaranzie.invalidita.enabled) {
      totale += 25;
    }
    if (vitaGaranzie.malattiaGrave.enabled) {
      totale += 86;
    }
    if (vitaGaranzie.inabilita.enabled) {
      totale += 28;
    }
    return totale;
  };
  
  const handleContinuaDatiPersonali = () => {
    // Passa allo step 3 (dati personali finali)
    setVitaStep(2);
  };
  
  const handleFinalSubmit = () => {
    const newErrors = {
      ...errors,
      nome: !formData.nome,
      cognome: !formData.cognome,
      email: !formData.email,
      telefono: !formData.telefono,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    console.log("Preventivo Vita Completo:", {
      formData,
      garanzie: vitaGaranzie,
      premioTotale: calcolaPremioTotale(),
    });
    
    // Qui invieresti i dati al backend per il checkout
    handleCloseVitaDialog();
  };

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData({
      ...formData,
      garanzie: { ...formData.garanzie, [field]: event.target.checked },
    });
  };

  const handleSubmitPreventivo = () => {
    // Validazione campi obbligatori step 2
    const newErrors = {
      ...errors,
      tipoAbitazione: !formData.tipoAbitazione,
      metriQuadri: !formData.metriQuadri,
      annoCostruzione: !formData.annoCostruzione,
    };

    setErrors(newErrors);

    // Se ci sono errori nello step 2, non procedere
    if (
      newErrors.tipoAbitazione ||
      newErrors.metriQuadri ||
      newErrors.annoCostruzione
    ) {
      return;
    }

    console.log("Preventivo richiesto:", formData);
    // Qui invierai i dati al backend
    handleCloseCasaDialog();
  };

  const handleContinua = () => {
    // Validazione campi obbligatori
    const newErrors = {
      nome: !formData.nome,
      cognome: !formData.cognome,
      email: !formData.email,
      telefono: !formData.telefono,
    };

    setErrors(newErrors);

    // Se ci sono errori, non procedere
    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    console.log("Form data:", formData);
    setCasaStep(1);
  };

  const handleIndietro = () => {
    if (casaStep > 0) {
      setCasaStep(casaStep - 1);
    }
  };

  return (
    <Box>
      <Helmet title="Insurance Prototype" />

      <Grid container spacing={3} mb={3}>
        {kpiCards.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.title}>
            <GradientCard gradient={item.gradient}>
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack spacing={1}>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="h3" fontWeight={800}>
                      {item.value}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      {item.helper}
                    </Typography>
                  </Stack>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.16)",
                      width: 56,
                      height: 56,
                    }}
                  >
                    {item.icon}
                  </Avatar>
                </Stack>
              </CardContent>
            </GradientCard>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={8}>
          <SectionCard
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                justifyContent: "center",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <CalendarMonthRoundedIcon color="warning" />
                <Typography variant="h5" fontWeight={700}>
                  Polizze in Scadenza
                </Typography>
              </Stack>
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1}
                flex={1}
              >
                <Avatar
                  sx={{
                    bgcolor: "#e8f6ef",
                    color: "#13b86d",
                    width: 72,
                    height: 72,
                  }}
                >
                  <ShieldRoundedIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight={700} align="center">
                  Tutte le polizze sono in regola!
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                >
                  Non ci sono polizze in scadenza nei prossimi 30 giorni.
                </Typography>
              </Stack>
            </CardContent>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard sx={{ height: "100%" }}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <NotificationsActiveRoundedIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    Notifiche Recenti
                  </Typography>
                  <Chip
                    label={notifications.length}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              </Stack>

              <Stack spacing={2}>
                {notifications.map((note) => (
                  <SectionCard
                    key={note.title}
                    variant="outlined"
                    sx={{
                      borderColor: "rgba(0,0,0,0.06)",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={0.5}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "rgba(255,144,70,0.12)",
                            color: "#ff6a00",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <AlarmRoundedIcon fontSize="small" />
                        </Avatar>
                        <Typography fontWeight={700} variant="subtitle1">
                          {note.title}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={1.5}
                      >
                        {note.body}
                      </Typography>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Chip
                          label={note.chip}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                        <Button
                          size="small"
                          endIcon={<ArrowForwardRoundedIcon />}
                        >
                          {note.cta}
                        </Button>
                      </Stack>
                    </CardContent>
                  </SectionCard>
                ))}
              </Stack>
            </CardContent>
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={8}>
          <SectionCard>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <TrendingUpRoundedIcon color="primary" />
                <Typography variant="h5" fontWeight={700}>
                  Suggerimenti Intelligenti
                </Typography>
                <Chip
                  label={`${suggestions.length} nuovi`}
                  size="small"
                  color="primary"
                />
              </Stack>

              <Stack spacing={3}>
                {suggestions.map((item) => (
                  <SectionCard
                    key={item.title}
                    variant="outlined"
                    sx={{
                      borderColor: "rgba(0,0,0,0.06)",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Stack spacing={2} flex={1} pr={2}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Avatar
                              sx={{
                                bgcolor: `${item.color}1a`,
                                color: item.color,
                              }}
                            >
                              {item.icon}
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>
                              {item.title}
                            </Typography>
                            <Chip
                              label={item.badge}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Stack>
                          <Typography variant="body1" color="text.primary">
                            Perche lo consigliamo:
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(28, 117, 255, 0.08)"
                                  : "#f3f7ff",
                              p: 2.5,
                              borderRadius: 1,
                            }}
                          >
                            {item.why}
                          </Typography>
                          <Typography variant="body1" color="text.primary">
                            I tuoi vantaggi:
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(19, 184, 109, 0.08)"
                                  : "#e9f7ef",
                              p: 2.5,
                              borderRadius: 1,
                            }}
                          >
                            {item.benefit}
                          </Typography>
                          <Button
                            variant="contained"
                            endIcon={<ArrowForwardRoundedIcon />}
                            onClick={
                              item.title === "Polizza Casa Consigliata"
                                ? handleOpenCasaDialog
                                : item.title === "Ottimizza la tua Polizza Vita"
                                ? handleOpenVitaDialog
                                : undefined
                            }
                            sx={{
                              alignSelf: "flex-start",
                              mt: 1,
                              bgcolor: item.color,
                              "&:hover": { bgcolor: item.color },
                            }}
                          >
                            Sono Interessato
                          </Button>
                        </Stack>
                        <IconButton size="small">
                          <CloseRoundedIcon />
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </SectionCard>
                ))}
              </Stack>
            </CardContent>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <SectionCard>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <EmojiEventsRoundedIcon color="warning" />
                  <Typography variant="h6" fontWeight={700}>
                    Loyalty Status
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="body1">Livello Attuale</Typography>
                  <Chip
                    label="Silver"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="body1">Punti Totali</Typography>
                  <Typography variant="h5" fontWeight={800}>
                    1250
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={33}
                  sx={{ height: 10, borderRadius: 999, mb: 1.5 }}
                />
                <Typography variant="body2" color="text.secondary" mb={2}>
                  3750 punti al livello Gold
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RedeemRoundedIcon />}
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  Esplora Premi
                </Button>
              </CardContent>
            </SectionCard>

            <SectionCard>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <RedeemRoundedIcon color="success" />
                  <Typography variant="h6" fontWeight={700}>
                    Premi Riscattati
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  {redeemed.map((item) => (
                    <SectionCard
                      key={item.title}
                      variant="outlined"
                      sx={{
                        borderColor: "rgba(0,0,0,0.06)",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mb={0.5}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "rgba(76,175,80,0.12)",
                              color: "#2e7d32",
                              width: 32,
                              height: 32,
                            }}
                          >
                            <RedeemRoundedIcon fontSize="small" />
                          </Avatar>
                          <Typography fontWeight={700}>{item.title}</Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={1}
                        >
                          {item.date}
                        </Typography>
                        <Chip
                          label={item.status}
                          size="small"
                          color={
                            item.status === "consegnato" ? "success" : "warning"
                          }
                          variant="outlined"
                        />
                      </CardContent>
                    </SectionCard>
                  ))}
                </Stack>
              </CardContent>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      {/* Dialog Assicurazione Casa */}
      <Dialog
        open={casaDialogOpen}
        onClose={handleCloseCasaDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#1e2432" : "#f0f4f8",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              background: "linear-gradient(135deg, #1c75ff, #4b9dff)",
              color: "white",
              py: 4,
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Assicurazione Casa
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Proteggi la tua abitazione e risparmia fino al 35%
            </Typography>
          </Box>

          <Box sx={{ px: 3, pt: 3, maxWidth: 350, mx: "auto" }}>
            <Stepper activeStep={casaStep} sx={{ mb: 4 }}>
              <Step>
                <StepLabel>I Tuoi Dati</StepLabel>
              </Step>
              <Step>
                <StepLabel>Dettagli Abitazione</StepLabel>
              </Step>
            </Stepper>
          </Box>

          <Card
            sx={{
              mx: 3,
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {casaStep === 0 && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" mb={8}>
                    <HomeRoundedIcon color="primary" fontSize="large" />
                    <Typography variant="h5" fontWeight={700}>
                      I Tuoi Dati
                    </Typography>
                  </Stack>

                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nome"
                        placeholder="Il tuo nome"
                        value={formData.nome}
                        onChange={handleInputChange("nome")}
                        variant="outlined"
                        required
                        error={errors.nome}
                        helperText={errors.nome ? "Inserisci Nome" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Cognome"
                        placeholder="Il tuo cognome"
                        value={formData.cognome}
                        onChange={handleInputChange("cognome")}
                        variant="outlined"
                        required
                        error={errors.cognome}
                        helperText={errors.cognome ? "Inserisci Cognome" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        placeholder="la.tua@email.com"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange("email")}
                        variant="outlined"
                        required
                        error={errors.email}
                        helperText={errors.email ? "Inserisci Email" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Telefono"
                        placeholder="+39 123 456 7890"
                        value={formData.telefono}
                        onChange={handleInputChange("telefono")}
                        variant="outlined"
                        required
                        error={errors.telefono}
                        helperText={errors.telefono ? "Inserisci Telefono" : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Provincia</InputLabel>
                        <Select
                          value={formData.provincia}
                          label="Provincia"
                          onChange={handleInputChange("provincia")}
                        >
                          <MenuItem value="">
                            <em>Seleziona provincia</em>
                          </MenuItem>
                          <MenuItem value="MI">Milano</MenuItem>
                          <MenuItem value="RM">Roma</MenuItem>
                          <MenuItem value="NA">Napoli</MenuItem>
                          <MenuItem value="TO">Torino</MenuItem>
                          <MenuItem value="FI">Firenze</MenuItem>
                          <MenuItem value="BO">Bologna</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Indirizzo"
                        placeholder="Via Roma 123"
                        value={formData.indirizzo}
                        onChange={handleInputChange("indirizzo")}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={3}
                  >
                    <Button
                      variant="text"
                      onClick={handleCloseCasaDialog}
                      sx={{ color: "text.secondary" }}
                    >
                      Indietro
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardRoundedIcon />}
                      onClick={handleContinua}
                      size="large"
                    >
                      Continua
                    </Button>
                  </Stack>
                </Box>
              )}

              {casaStep === 1 && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" mb={8}>
                    <HomeRoundedIcon color="primary" fontSize="large" />
                    <Typography variant="h5" fontWeight={700}>
                      La Tua Abitazione
                    </Typography>
                  </Stack>

                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        required
                        error={errors.tipoAbitazione}
                        size="small"
                      >
                        <InputLabel>Tipo di Abitazione</InputLabel>
                        <Select
                          value={formData.tipoAbitazione}
                          label="Tipo di Abitazione"
                          onChange={handleInputChange("tipoAbitazione")}
                        >
                          <MenuItem value="Appartamento">Appartamento</MenuItem>
                          <MenuItem value="Villa">Villa</MenuItem>
                          <MenuItem value="Villetta">Villetta</MenuItem>
                          <MenuItem value="Attico">Attico</MenuItem>
                          <MenuItem value="Loft">Loft</MenuItem>
                        </Select>
                        {errors.tipoAbitazione && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, ml: 1.75 }}
                          >
                            Inserisci Tipo di Abitazione
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Metri Quadri"
                        placeholder="Es. 100"
                        type="number"
                        value={formData.metriQuadri}
                        onChange={handleInputChange("metriQuadri")}
                        variant="outlined"
                        required
                        error={errors.metriQuadri}
                        helperText={
                          errors.metriQuadri ? "Inserisci Metri Quadri" : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Anno di Costruzione"
                        placeholder="Es. 1990"
                        type="number"
                        value={formData.annoCostruzione}
                        onChange={handleInputChange("annoCostruzione")}
                        variant="outlined"
                        required
                        error={errors.annoCostruzione}
                        helperText={
                          errors.annoCostruzione
                            ? "Inserisci Anno di Costruzione"
                            : ""
                        }
                      />
                    </Grid>
                  </Grid>

                  <Box mt={8}>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Garanzie Desiderate
                    </Typography>
                    <FormGroup>
                      <Grid container>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.garanzie.incendio}
                                onChange={handleCheckboxChange("incendio")}
                              />
                            }
                            label="Incendio"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.garanzie.furto}
                                onChange={handleCheckboxChange("furto")}
                              />
                            }
                            label="Furto"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.garanzie.danniAcqua}
                                onChange={handleCheckboxChange("danniAcqua")}
                              />
                            }
                            label="Danni da Acqua"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.garanzie.responsabilitaCivile}
                                onChange={handleCheckboxChange(
                                  "responsabilitaCivile",
                                )}
                              />
                            }
                            label="Responsabilità Civile"
                          />
                        </Grid>
                      </Grid>
                    </FormGroup>
                  </Box>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={8}
                  >
                    <Button
                      variant="text"
                      onClick={handleIndietro}
                      sx={{ color: "text.secondary" }}
                    >
                      Indietro
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardRoundedIcon />}
                      onClick={handleSubmitPreventivo}
                      size="large"
                      sx={{
                        bgcolor: "#4caf50",
                        "&:hover": { bgcolor: "#45a049" },
                      }}
                    >
                      Ottieni Preventivi Gratuiti
                    </Button>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          <Grid container spacing={2} sx={{ px: 3, pb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  textAlign: "center",
                  py: 3,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "white",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(28, 117, 255, 0.12)",
                    color: "#1c75ff",
                    width: 56,
                    height: 56,
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <ShieldRoundedIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  100% Gratuito
                </Typography>
                <Typography variant="body2" color="text.secondary" px={2}>
                  Nessun costo nascosto, confronto sempre gratuito
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  textAlign: "center",
                  py: 3,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "white",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(255, 152, 0, 0.12)",
                    color: "#ff9800",
                    width: 56,
                    height: 56,
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <EmojiEventsRoundedIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Migliori Compagnie
                </Typography>
                <Typography variant="body2" color="text.secondary" px={2}>
                  Confrontiamo solo le compagnie più affidabili
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  textAlign: "center",
                  py: 3,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "white",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(76, 175, 80, 0.12)",
                    color: "#4caf50",
                    width: 56,
                    height: 56,
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <AlarmRoundedIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Risultato Immediato
                </Typography>
                <Typography variant="body2" color="text.secondary" px={2}>
                  Ricevi le migliori offerte in pochi secondi
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Dialog Polizza Vita */}
      <Dialog
        open={vitaDialogOpen}
        onClose={handleCloseVitaDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#1e2432" : "#f0f4f8",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              background: "linear-gradient(135deg, #13b86d, #10ac7d)",
              color: "white",
              py: 4,
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              {vitaStep === 0 && "Mobility Life per te Vita"}
              {vitaStep === 1 && "Personalizza le tue Garanzie"}
              {vitaStep === 2 && "Completa i tuoi Dati"}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
              {vitaStep === 0 && "Polizza vita modulare 100% online"}
              {vitaStep === 1 && "Età: 18 anni - Regola capitale e durata"}
              {vitaStep === 2 && "Ultimi passi per il preventivo finale"}
            </Typography>

            {vitaStep === 0 && (
              <Grid container spacing={8} justifyContent="center">{/*... existing feature points ...*/}
                <Grid item xs={12} md={4}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        minWidth: 20,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="white"
                        fontWeight={700}
                      >
                        ✓
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                      Premio fisso per tutta la durata
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        minWidth: 20,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="white"
                        fontWeight={700}
                      >
                        ✓
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                      Fino a 300.000€ di capitale
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        minWidth: 20,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="white"
                        fontWeight={700}
                      >
                        ✓
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                      Sottoscrizione online fino a 54 anni
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Box>

          {vitaStep === 0 && (
            <>
              <Card
                sx={{
                  mx: 3,
                  mt: 3,
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                    <FavoriteRoundedIcon color="success" fontSize="large" />
                    <Typography variant="h5" fontWeight={700}>
                      Calcola il tuo Preventivo
                    </Typography>
                  </Stack>

                  <TextField
                    fullWidth
                    label="Data di Nascita"
                    type="date"
                    value={formData.dataNascita}
                    onChange={handleInputChange("dataNascita")}
                    variant="outlined"
                    required
                    error={errors.dataNascita}
                    helperText={
                      errors.dataNascita
                        ? "Inserisci Data di Nascita"
                        : "Età compresa tra 18 e 74 anni"
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: new Date(
                        new Date().setFullYear(new Date().getFullYear() - 18),
                      )
                        .toISOString()
                        .split("T")[0],
                      min: new Date(
                        new Date().setFullYear(new Date().getFullYear() - 74),
                      )
                        .toISOString()
                        .split("T")[0],
                    }}
                    sx={{ mb: 2 }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.privacyAccettata}
                        onChange={handleCheckboxPrivacyChange}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Ho letto e accetto l'
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: "#13b86d", fontWeight: 600 }}
                        >
                          informativa sulla privacy
                        </Typography>
                      </Typography>
                    }
                    sx={{ mb: 2 }}
                  />
                  {errors.privacyAccettata && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mb: 2, ml: 1.75 }}
                    >
                      Devi accettare l'informativa sulla privacy
                    </Typography>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleCalcolaPreventivo}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      bgcolor: "#13b86d",
                      "&:hover": { bgcolor: "#10ac7d" },
                      py: 1.5,
                    }}
                  >
                    Calcola Preventivo
                  </Button>
                </CardContent>
              </Card>

              <Grid container spacing={2} sx={{ px: 3, pb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      textAlign: "center",
                      py: 3,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "white",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "rgba(19, 184, 109, 0.12)",
                        color: "#13b86d",
                        width: 56,
                        height: 56,
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <ShieldRoundedIcon fontSize="large" />
                    </Avatar>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Decesso
                    </Typography>
                    <Typography variant="body2" color="text.secondary" px={2}>
                      Capitale assicurato ai tuoi cari
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      textAlign: "center",
                      py: 3,
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "white",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "rgba(19, 184, 109, 0.12)",
                        color: "#13b86d",
                        width: 56,
                        height: 56,
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <ShieldRoundedIcon fontSize="large" />
                    </Avatar>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Invalidità Totale Permanente
                    </Typography>
                    <Typography variant="body2" color="text.secondary" px={2}>
                      Capitale uguale al decesso
                    </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card
                sx={{
                  textAlign: "center",
                  py: 3,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "white",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(19, 184, 109, 0.12)",
                    color: "#13b86d",
                    width: 56,
                    height: 56,
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <ShieldRoundedIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Malattia Grave
                </Typography>
                <Typography variant="body2" color="text.secondary" px={2}>
                  Capitale variabile in base al decesso
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card
                sx={{
                  textAlign: "center",
                  py: 3,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "white",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(19, 184, 109, 0.12)",
                    color: "#13b86d",
                    width: 56,
                    height: 56,
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <ShieldRoundedIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Inabilità Totale Temporanea
                </Typography>
                <Typography variant="body2" color="text.secondary" px={2}>
                  Capitale pari all'1% del decesso
                </Typography>
              </Card>
            </Grid>
          </Grid>
            </>
          )}

          {vitaStep === 1 && (
            <Box sx={{ p: 3 }}>
              {/* Card Decesso - Principale Arancione */}
              <Card
                sx={{
                  mb: 2,
                  background: "linear-gradient(135deg, #13b86d, #10ac7d)",
                  color: "white",
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: "rgba(255,255,255,0.3)" }}>
                        <FavoriteRoundedIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Decesso
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          Durata: {vitaGaranzie.decesso.durata} anni - Capitale: {vitaGaranzie.decesso.capitale.toLocaleString()}€
                        </Typography>
                      </Box>
                    </Stack>
                    <Box textAlign="right">
                      <Typography variant="h4" fontWeight={700}>
                        180.00€
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        all'anno
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3, px: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight={600}>
                        Durata
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {vitaGaranzie.decesso.durata} anni
                      </Typography>
                    </Box>
                    <Slider
                      value={vitaGaranzie.decesso.durata}
                      onChange={handleSliderChange('decesso', 'durata')}
                      min={5}
                      max={20}
                      step={1}
                      sx={{
                        color: 'white',
                      }}
                    />
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        5 anni
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        20 anni
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ px: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight={600}>
                        Capitale assicurato ai tuoi cari
                      </Typography>
                      <Chip
                        label={`${vitaGaranzie.decesso.capitale.toLocaleString()} €`}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.3)',
                          color: 'white',
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                    <Slider
                      value={vitaGaranzie.decesso.capitale}
                      onChange={handleSliderChange('decesso', 'capitale')}
                      min={50000}
                      max={300000}
                      step={10000}
                      sx={{
                        color: 'white',
                      }}
                    />
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        50.000 €
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        300.000 €
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Card Invalidità Totale Permanente */}
              <Card
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  border: vitaGaranzie.invalidita.enabled 
                    ? '2px solid #13b86d' 
                    : '2px solid transparent',
                  bgcolor: (theme) => vitaGaranzie.invalidita.enabled
                    ? (theme.palette.mode === "dark" ? "rgba(19, 184, 109, 0.15)" : "rgba(19, 184, 109, 0.08)")
                    : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f8f9fa"),
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Switch
                        checked={vitaGaranzie.invalidita.enabled}
                        onChange={handleGaranziaToggle('invalidita')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#13b86d',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#13b86d',
                          },
                        }}
                      />
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Invalidità Totale Permanente
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Durata: {vitaGaranzie.invalidita.durata} anni - Capitale: {vitaGaranzie.invalidita.capitale.toLocaleString()}€
                        </Typography>
                      </Box>
                    </Stack>
                    <Box textAlign="right">
                      <Typography variant="h5" fontWeight={700}>
                        25.00€
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        all'anno
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Card Malattia Grave */}
              <Card
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  border: vitaGaranzie.malattiaGrave.enabled 
                    ? '2px solid #13b86d' 
                    : '2px solid transparent',
                  bgcolor: (theme) => vitaGaranzie.malattiaGrave.enabled
                    ? (theme.palette.mode === "dark" ? "rgba(19, 184, 109, 0.15)" : "rgba(19, 184, 109, 0.08)")
                    : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f8f9fa"),
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Switch
                        checked={vitaGaranzie.malattiaGrave.enabled}
                        onChange={handleGaranziaToggle('malattiaGrave')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#13b86d',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#13b86d',
                          },
                        }}
                      />
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Malattia Grave
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Durata: {vitaGaranzie.malattiaGrave.durata} anni - Capitale: {vitaGaranzie.malattiaGrave.capitale.toLocaleString()}€
                        </Typography>
                      </Box>
                    </Stack>
                    <Box textAlign="right">
                      <Typography variant="h5" fontWeight={700}>
                        86.00€
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        all'anno
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Card Inabilità Totale Temporanea */}
              <Card
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  border: vitaGaranzie.inabilita.enabled 
                    ? '2px solid #13b86d' 
                    : '2px solid transparent',
                  bgcolor: (theme) => vitaGaranzie.inabilita.enabled
                    ? (theme.palette.mode === "dark" ? "rgba(19, 184, 109, 0.15)" : "rgba(19, 184, 109, 0.08)")
                    : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f8f9fa"),
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Switch
                        checked={vitaGaranzie.inabilita.enabled}
                        onChange={handleGaranziaToggle('inabilita')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#13b86d',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#13b86d',
                          },
                        }}
                      />
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Inabilità Totale Temporanea
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Durata: {vitaGaranzie.inabilita.durata} anni - Capitale: {vitaGaranzie.inabilita.capitale.toLocaleString()}€
                        </Typography>
                      </Box>
                    </Stack>
                    <Box textAlign="right">
                      <Typography variant="h5" fontWeight={700}>
                        28.00€
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        all'anno
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Premio Totale */}
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  border: "2px solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark" ? "#13b86d" : "#13b86d",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h5" fontWeight={700}>
                        Premio Totale
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Somma di tutte le garanzie selezionate
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography
                        variant="h3"
                        fontWeight={700}
                        sx={{ color: "#13b86d" }}
                      >
                        {calcolaPremioTotale()}.00€
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        all'anno
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {(calcolaPremioTotale() / 12).toFixed(2)}€/mese
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => setVitaStep(0)}
                      sx={{
                        flex: 1,
                        borderColor: "#13b86d",
                        color: "#13b86d",
                        "&:hover": { borderColor: "#10ac7d", bgcolor: "rgba(19, 184, 109, 0.08)" },
                        py: 1.5,
                        fontSize: "1.1rem",
                      }}
                    >
                      Indietro
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleContinuaDatiPersonali}
                      endIcon={<ArrowForwardRoundedIcon />}
                      sx={{
                        flex: 2,
                        bgcolor: "#13b86d",
                        "&:hover": { bgcolor: "#10ac7d" },
                        py: 1.5,
                        fontSize: "1.1rem",
                      }}
                    >
                      Continua con i Dati Personali
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )}

          {vitaStep === 2 && (
            <Box sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                <Avatar sx={{ bgcolor: "#13b86d", width: 48, height: 48 }}>
                  <RedeemRoundedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Dati Personali
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome"
                    placeholder="Il tuo nome"
                    value={formData.nome}
                    onChange={handleInputChange("nome")}
                    required
                    error={errors.nome}
                    helperText={errors.nome ? "Campo obbligatorio" : ""}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cognome"
                    placeholder="Il tuo cognome"
                    value={formData.cognome}
                    onChange={handleInputChange("cognome")}
                    required
                    error={errors.cognome}
                    helperText={errors.cognome ? "Campo obbligatorio" : ""}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    placeholder="la.tua@email.com"
                    value={formData.email}
                    onChange={handleInputChange("email")}
                    required
                    error={errors.email}
                    helperText={errors.email ? "Campo obbligatorio" : ""}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Telefono"
                    placeholder="+39 123 456 7890"
                    value={formData.telefono}
                    onChange={handleInputChange("telefono")}
                    required
                    error={errors.telefono}
                    helperText={errors.telefono ? "Campo obbligatorio" : ""}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Codice Fiscale"
                    placeholder="RSSMRA80A01H501Z"
                    value={formData.codiceFiscale}
                    onChange={handleInputChange("codiceFiscale")}
                    variant="outlined"
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </Grid>
              </Grid>

              {/* Riepilogo Coperture */}
              <Card
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(19, 184, 109, 0.15)"
                      : "rgba(19, 184, 109, 0.08)",
                  border: "1px solid #13b86d",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2} sx={{ color: "#13b86d" }}>
                    Riepilogo Coperture
                  </Typography>

                  {vitaGaranzie.decesso.enabled && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        Decesso ({vitaGaranzie.decesso.durata} anni - {vitaGaranzie.decesso.capitale.toLocaleString()}€)
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        180.00€
                      </Typography>
                    </Box>
                  )}
                  {vitaGaranzie.invalidita.enabled && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        Invalidità Totale Permanente ({vitaGaranzie.invalidita.durata} anni - {vitaGaranzie.invalidita.capitale.toLocaleString()}€)
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        25.00€
                      </Typography>
                    </Box>
                  )}
                  {vitaGaranzie.malattiaGrave.enabled && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        Malattia Grave ({vitaGaranzie.malattiaGrave.durata} anni - {vitaGaranzie.malattiaGrave.capitale.toLocaleString()}€)
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        86.00€
                      </Typography>
                    </Box>
                  )}
                  {vitaGaranzie.inabilita.enabled && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        Inabilità Totale Temporanea ({vitaGaranzie.inabilita.durata} anni - {vitaGaranzie.inabilita.capitale.toLocaleString()}€)
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        28.00€
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" fontWeight={700}>
                      Totale Annuo
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: "#13b86d" }}>
                      {calcolaPremioTotale()}.00€
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Bottone Checkout */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setVitaStep(1)}
                  sx={{
                    flex: 1,
                    borderColor: "#13b86d",
                    color: "#13b86d",
                    "&:hover": { borderColor: "#10ac7d", bgcolor: "rgba(19, 184, 109, 0.08)" },
                    py: 1.5,
                    fontSize: "1.1rem",
                  }}
                >
                  Indietro
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleFinalSubmit}
                  startIcon={<FavoriteRoundedIcon />}
                  sx={{
                    flex: 2,
                    bgcolor: "#13b86d",
                    "&:hover": { bgcolor: "#10ac7d" },
                    py: 1.5,
                    fontSize: "1.1rem",
                  }}
                >
                  Procedi al Checkout - {calcolaPremioTotale()}.00€
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Personale;
